import type { ComponentType } from "react";
import {
  BarChart3,
  BookOpen,
  Building2,
  ChevronLeft,
  LayoutDashboard,
  MessageSquare,
  Receipt,
  Settings,
  Shield,
  Signal,
  UserCog,
  UserPlus,
  Users,
  UsersRound,
  Wrench,
} from "lucide-react";
import { useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import { AppLogo } from "@/components/common/app-logo";
import { isGlobalAdmin, isPlatformUser, orgRoleRank } from "@/features/auth/roles";
import { type AuthUser, useAuthStore } from "@/features/auth/store/auth-store";
import { useChatStore } from "@/features/chat/store/chat-store";
import { FirmSwitcher } from "@/features/firm-context/components/firm-switcher";
import { useFirmContextStore } from "@/features/firm-context/store/firm-context-store";
import {
  ONBOARDING_STEP_COUNT,
  onboardingProgressSummary,
  useOnboardingProgressStore,
} from "@/features/onboarding/store/onboarding-progress-store";
import { useUIStore } from "@/store/ui-store";
import { cn } from "@/lib/utils";

interface NavItem {
  label: string;
  /** House-org (NSI CAP) label override — e.g. "CAP Clients" in the house view,
   * plain "Clients" in a Business-Practitioner firm's view. */
  capLabel?: string;
  icon: ComponentType<{ className?: string }>;
  to: string;
  /** Minimum org-role rank required to SEE this item (see `orgRoleRank`). */
  minRank: number;
  /** Only the single NSI house org sees this (e.g. the BP directory). */
  houseOnly?: boolean;
  /** Only platform admins (GA/SA) see this. */
  platformOnly?: boolean;
  /** In the NSI (house) org this is hidden from everyone below a platform admin
   * (so a house Country Director doesn't see it); a Business-Practitioner firm's
   * own users still get it per `minRank`. Used for the Team roster. */
  houseHiddenBelowPlatform?: boolean;
  /** Hidden from a top-level Global Admin (Django superuser) — they operate the
   * Control Center, not a firm's practice dashboard. */
  hideForGlobalAdmin?: boolean;
  /** Clear any active Business-Practitioner workspace when clicked, so this tab
   * always lands on the house (CAP) book — e.g. "CAP Clients". */
  resetFirmContext?: boolean;
  /** Extra paths (beyond `to`) that should mark this item active. */
  activeWhen?: (pathname: string) => boolean;
  /** Show the chat unread badge on this item. */
  badge?: "chat";
  /** Extra opt-in: also show this item to a user who fails the rank/flag gates
   * above but matches this predicate (e.g. a house Staff assigned to a BP sees
   * "Business Practitioners"). Must be self-contained — it bypasses the gates. */
  alsoVisibleIf?: (user: AuthUser | null) => boolean;
}

/** The role/org-aware workspace nav (Miro "NSI CAP" vs "Business Practitioner"
 * views). Order matches the Miro; each item is gated by role rank and, where
 * noted, by org type / platform flag. The house view shows the `capLabel`
 * (e.g. "CAP Clients") and the GA/SA-only "Business Practitioner" directory;
 * a Business-Practitioner firm sees the plain labels and no BP directory. */
const NAV_ITEMS: NavItem[] = [
  { label: "Dashboard", icon: LayoutDashboard, to: "/dashboard", minRank: 1, hideForGlobalAdmin: true },
  { label: "New Arrival", icon: UserPlus, to: "/new-arrivals", minRank: 3 },
  { label: "Practitioner", capLabel: "CAP Practitioner", icon: UserCog, to: "/practitioners", minRank: 2 },
  {
    label: "Clients",
    capLabel: "CAP Clients",
    icon: Users,
    to: "/clients",
    minRank: 1,
    // "CAP Clients" is the house direct book — clicking it drops any active BP
    // workspace so a BP client can't linger under this tab (QA: keep the CAP and
    // BP client books visibly separate).
    resetFirmContext: true,
  },
  {
    // House view only — the BP directory, reached as the "Business Practitioners"
    // tab: pick a Business Practitioner and drill into its clients (kept distinct
    // from the CAP direct book above). GA/SA see every BP; a house Team-Leader+
    // sees the BPs assigned to them (scoped server-side); a house Staff member
    // sees it too once they've been assigned to at least one BP. Hidden for BPs.
    label: "Business Practitioners",
    icon: Building2,
    to: "/business-practitioners",
    minRank: 2,
    houseOnly: true,
    alsoVisibleIf: (user) =>
      user?.firm?.org_type === "house" && (user?.assigned_bp_count ?? 0) > 0,
  },
  // House org: roster is GA/SA-only (leadership rows + access levels stay private
  // from a house Country Director). A BP firm's own CD+ keeps it for their team.
  { label: "Team", icon: UsersRound, to: "/team", minRank: 3, houseHiddenBelowPlatform: true },
  { label: "Chat", icon: MessageSquare, to: "/chat", minRank: 1, badge: "chat" },
  { label: "Invoices", icon: Receipt, to: "/invoices", minRank: 3 },
  { label: "Reports", icon: BarChart3, to: "/reports", minRank: 3 },
  { label: "e-Services", icon: Signal, to: "/eservices", minRank: 3 },
  { label: "Knowledge Base", icon: BookOpen, to: "/kb", minRank: 1 },
];

/** Resolve the nav items visible to `user`, with org-aware labels. Exported for
 * unit-testing the role gating without rendering the full router/store shell. */
export function visibleNavItems(user: AuthUser | null): (NavItem & { label: string })[] {
  const rank = orgRoleRank(user);
  const isHouse = user?.firm?.org_type === "house";
  const isPlatform = isPlatformUser(user);
  const isGa = isGlobalAdmin(user);
  return NAV_ITEMS.filter((item) => {
    const baseOk =
      rank >= item.minRank &&
      (!item.platformOnly || isPlatform) &&
      (!item.houseOnly || isHouse) &&
      (!item.houseHiddenBelowPlatform || !isHouse || isPlatform) &&
      (!item.hideForGlobalAdmin || !isGa);
    return baseOk || (item.alsoVisibleIf?.(user) ?? false);
  }).map((item) => ({ ...item, label: isHouse && item.capLabel ? item.capLabel : item.label }));
}

interface AppSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export function AppSidebar({ collapsed, onToggle }: AppSidebarProps) {
  const user = useAuthStore((s) => s.user);
  const isPlatformAdmin = useAuthStore((s) => s.user?.is_platform_admin ?? false);
  const pathname = useLocation().pathname;
  const onOps = pathname.startsWith("/admin/ops");
  const unreadTotal = useChatStore((s) => s.unreadTotal);
  const mobileNavOpen = useUIStore((s) => s.mobileNavOpen);
  const clearFirm = useFirmContextStore((s) => s.clearFirm);

  const rank = orgRoleRank(user);
  const navItems = visibleNavItems(user);
  const canSeeSettings = rank >= 3;
  // Only an explicit `false` means setup is unfinished (matches OnboardingGate).
  const onboardingDone = user?.firm?.onboarding_completed !== false;

  // Live "X of 6 done" for the Getting-started pill — derived once from existing
  // data (see onboarding-progress-store). Load only while setup is unfinished.
  const progress = useOnboardingProgressStore(
    useShallow((s) => ({ done: s.done, skipped: s.skipped, loaded: s.loaded, load: s.load })),
  );
  useEffect(() => {
    if (!onboardingDone) progress.load();
  }, [onboardingDone, progress.load]);
  const { doneCount, skippedCount } = onboardingProgressSummary(progress);
  const onboardingSubtitle = progress.loaded
    ? `${doneCount} of ${ONBOARDING_STEP_COUNT} done${skippedCount > 0 ? ` · ${skippedCount} skipped` : ""}`
    : "Finish setting up your firm";

  const navLinkClass = (isActive: boolean) =>
    cn(
      "relative flex items-center gap-3 rounded-sm px-3 py-2.5 text-sm transition-all",
      collapsed && "justify-center px-0",
      isActive
        ? "bg-sidebar-active font-semibold text-white"
        : "text-sidebar-foreground hover:bg-sidebar-hover font-medium hover:text-white",
    );
  const activeBar = (
    <span className="bg-gold absolute top-1/2 left-0 h-[18px] w-[3px] -translate-y-1/2 rounded-full" />
  );

  return (
    <nav
      aria-label="Main"
      className={cn(
        // Off-canvas drawer below lg (slides in on the header hamburger); a
        // fixed, always-visible rail at lg+ that can collapse to an icon strip.
        // Navigating a link closes the drawer via the route-change effect in
        // AppLayout; the backdrop + Escape also close it.
        "bg-sidebar fixed top-0 left-0 z-40 flex h-full w-[248px] flex-col overflow-hidden px-4 py-6 transition-transform duration-300 lg:z-20 lg:translate-x-0 lg:transition-[width]",
        mobileNavOpen ? "translate-x-0" : "-translate-x-full",
        collapsed ? "lg:w-20" : "lg:w-[248px]",
      )}
    >
      {/* Logo lockup */}
      <div className={cn("mb-5 flex h-10 items-center px-1.5", collapsed && "justify-center px-0")}>
        <AppLogo variant="sidebar" showWordmark={!collapsed} />
        {!collapsed && (
          <button
            type="button"
            onClick={onToggle}
            aria-label="Collapse sidebar"
            className="text-sidebar-muted hover:bg-sidebar-hover ml-auto hidden rounded-lg p-1.5 transition-colors hover:text-white lg:block"
          >
            <ChevronLeft className="size-4" />
          </button>
        )}
      </div>

      {collapsed && (
        <button
          type="button"
          onClick={onToggle}
          aria-label="Expand sidebar"
          className="text-sidebar-muted hover:bg-sidebar-hover mx-auto mb-4 rounded-lg p-1.5 transition-colors hover:text-white"
        >
          <ChevronLeft className="size-4 rotate-180" />
        </button>
      )}

      {!collapsed && (
        <span className="text-sidebar-muted px-3 pb-1 text-[11px] font-semibold tracking-[0.1em] uppercase">
          Workspace
        </span>
      )}

      <div className="scrollbar-hide flex flex-1 flex-col gap-1 overflow-y-auto">
        {/* Getting started — shown until the firm finishes onboarding. */}
        {!onboardingDone && (
          <NavLink
            to="/onboarding"
            className={cn(
              "mb-1 flex items-center gap-3 rounded-sm border border-[color:rgb(242_140_40_/_0.35)] bg-[color:rgb(242_140_40_/_0.14)] px-3 py-2.5 text-sm font-semibold text-white",
              collapsed && "justify-center px-0",
            )}
          >
            <span
              className="relative flex size-[22px] shrink-0 items-center justify-center rounded-full"
              style={{
                background:
                  "conic-gradient(var(--primary) 0 33%, rgb(255 255 255 / 0.15) 33% 100%)",
              }}
            >
              <span className="bg-sidebar size-[14px] rounded-full" />
            </span>
            {!collapsed && (
              <span className="flex min-w-0 flex-col leading-tight">
                <span>Getting started</span>
                <span className="truncate text-[11px] font-medium text-[#ffd9ae]">
                  {onboardingSubtitle}
                </span>
              </span>
            )}
          </NavLink>
        )}

        {navItems.map((item) => {
          const showBadge = item.badge === "chat" && unreadTotal > 0;
          const forcedActive = item.activeWhen?.(pathname) ?? false;
          return (
            <NavLink
              key={item.label}
              to={item.to}
              onClick={() => item.resetFirmContext && clearFirm()}
              className={({ isActive }) => navLinkClass(isActive || forcedActive)}
            >
              {({ isActive }) => (
                <>
                  {(isActive || forcedActive) && !collapsed && activeBar}
                  <span className="relative shrink-0">
                    <item.icon className="size-[18px] shrink-0" />
                    {showBadge && collapsed && (
                      <span className="bg-primary absolute -top-1 -right-1 size-2 rounded-full" />
                    )}
                  </span>
                  {!collapsed && <span className="truncate">{item.label}</span>}
                  {showBadge && !collapsed && (
                    <span className="bg-primary ml-auto flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-semibold text-white">
                      {unreadTotal > 99 ? "99+" : unreadTotal}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          );
        })}
      </div>

      <div className="mt-auto flex flex-col gap-1">
        {isPlatformAdmin && (
          <NavLink to="/admin" className={({ isActive }) => navLinkClass(isActive && !onOps)}>
            {({ isActive }) => (
              <>
                {isActive && !onOps && !collapsed && activeBar}
                <Shield className="size-[18px] shrink-0" />
                {!collapsed && <span className="truncate">Control Center</span>}
              </>
            )}
          </NavLink>
        )}
        {isPlatformAdmin && (
          <NavLink to="/admin/ops" className={({ isActive }) => navLinkClass(isActive)}>
            {({ isActive }) => (
              <>
                {isActive && !collapsed && activeBar}
                <Wrench className="size-[18px] shrink-0" />
                {!collapsed && <span className="truncate">Ops Tools</span>}
              </>
            )}
          </NavLink>
        )}

        {/* Firm workspace switcher — house TL/staff switch into an assigned BP;
            everyone else sees a plain card (Chat is a primary nav item above). */}
        <FirmSwitcher collapsed={collapsed} />

        {canSeeSettings && (
          <NavLink to="/settings" className={({ isActive }) => navLinkClass(isActive)}>
            {({ isActive }) => (
              <>
                {isActive && !collapsed && activeBar}
                <Settings className="size-[18px] shrink-0" />
                {!collapsed && <span className="truncate">Settings</span>}
              </>
            )}
          </NavLink>
        )}
      </div>
    </nav>
  );
}
